import { FileText, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DOCUMENT_CATEGORY_LABELS } from '@/entities/document/config/document-category-labels';
import { useDocuments } from '@/entities/document/hooks/use-documents';
import type { KitDocument } from '@/entities/document/schemas/kit-document';
import { RecordEditor } from '@/entities/shared/record/record-editor';
import { useEditorParam } from '@/shared/components/editor-sheet/use-editor-param';
import { EmptyState } from '@/shared/components/empty-state';
import { ListCard } from '@/shared/components/list/list-card';
import { ListSkeleton } from '@/shared/components/list/list-skeleton';
import { Page } from '@/shared/components/page/page';
import { PageHeader } from '@/shared/components/page/page-header';
import { SearchInput } from '@/shared/components/search-input';
import { useTranslation } from '@/shared/i18n/use-translation';
import { Button } from '@/shared/ui/button/button';
import { groupDocuments } from '../helpers/group-documents';
import { DocumentEditor } from './document-editor';
import { DocumentRow } from './document-row';

export function DocumentsScreen() {
  const { t } = useTranslation();
  const docs = useDocuments();
  const editor = useEditorParam();
  const [query, setQuery] = useState('');
  const groups = useMemo(() => groupDocuments(docs ?? [], query), [docs, query]);
  return (
    <Page>
      <PageHeader
        title={t('Documents')}
        description={t('Copies of the important paperwork, available offline on all your devices.')}
        primaryAction={{ label: t('Add'), onClick: () => editor.open('new') }}
      />
      {docs && docs.length > 0 && (
        <SearchInput placeholder={t('Search documents')} value={query} onChange={setQuery} />
      )}
      {!docs ? (
        <ListSkeleton />
      ) : docs.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={t('No documents yet')}
          description={t(
            'ID cards, passports, deeds, insurance policies, medical reports, prescriptions… Take a photo or upload the PDF.',
          )}
          action={
            <Button onClick={() => editor.open('new')}>
              <Plus /> {t('Add document')}
            </Button>
          }
        />
      ) : (
        groups.map((group) => (
          <section key={group.category} className="flex flex-col gap-2">
            <h2 className="text-sm font-medium text-muted-foreground">
              {t(DOCUMENT_CATEGORY_LABELS[group.category].label)}
            </h2>
            <ListCard>
              {group.docs.map((view) => (
                <DocumentRow key={view.id} view={view} onOpen={() => editor.open(view.id)} />
              ))}
            </ListCard>
          </section>
        ))
      )}
      <RecordEditor<KitDocument> collection="documents" id={editor.editing}>
        {(view) => <DocumentEditor view={view} docs={docs ?? []} onClose={editor.close} />}
      </RecordEditor>
    </Page>
  );
}
